import { AppButton, AppTooltip, useDataTableSort } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { FileDown } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { downloadExcel } from '@/lib/excel-utils'

/**
 * @param data - エクスポートするデータ
 * @param columns - カラム定義
 * @param filename - ファイル名（拡張子なし）
 * @param disabled - ボタンを無効化するか
 * @param isFiltered - フィルタリングされているか
 */
interface AppExcelDownloadButtonProps<T> {
  data: T[]
  columns: { label: string; accessor: (row: T) => unknown }[]
  filename: string
  disabled?: boolean
  isFiltered?: boolean
  /** ソートキー */
  sortKey?: string | null
  /** ソート方向 */
  sortDirection?: 'asc' | 'desc'
  /** ソート用カラム定義 */
  sortColumns?: { key: string; accessor?: (row: T) => unknown }[]
}

/**
 * AppExcelDownloadButton - Excelダウンロードボタンコンポーネント
 *
 * データをExcel形式でダウンロードするボタン。
 * タイムスタンプ付きのファイル名で自動ダウンロードします。
 *
 */
export function AppExcelDownloadButton<T>({
  data,
  columns,
  filename,
  disabled = false,
  isFiltered = false,
  sortKey,
  sortDirection = 'asc',
  sortColumns,
}: AppExcelDownloadButtonProps<T>) {
  const [isExporting, setIsExporting] = useState(false)

  // ソート設定を作成
  const sortConfig = sortKey ? { key: sortKey, direction: sortDirection } : null

  // useDataTableSortでソート（AppDataTableと同じロジック）
  const { sortedData } = useDataTableSort({
    data,
    columns: sortColumns || [],
    sortConfig,
  })

  // ソート用カラムがない場合は元データを使用
  const exportData = sortColumns ? sortedData : data

  const handleExport = () => {
    setIsExporting(true)

    setTimeout(() => {
      try {
        const timestamp = DateTime.local().toFormat('yyyyMMdd_HHmm')

        const filenameParts = [filename]
        if (isFiltered) {
          filenameParts.push('フィルタ済')
        }
        filenameParts.push(timestamp)

        const finalFilename = filenameParts.join('_')

        downloadExcel(exportData, columns, finalFilename)
      } finally {
        setIsExporting(false)
      }
    }, 100)
  }

  const tooltipText = isFiltered
    ? '検索で絞り込んだ結果をEXCEL形式でエクスポートできます'
    : '全データをEXCEL形式でエクスポートできます'

  return (
    <AppTooltip content={tooltipText}>
      <AppButton
        variant='outline'
        size='sm'
        onClick={handleExport}
        disabled={disabled || !data.length || isExporting}
        isPending={isExporting}
      >
        <FileDown className='text-success' />
        ダウンロード
      </AppButton>
    </AppTooltip>
  )
}
